/**
 * SUBTOTALS AND BAR WIDTHS OVER THE SERVER'S OWN EXACT INTEGERS.
 *
 * **WHY THIS FILE IS ALLOWED TO EXIST.** The rule in these screens is that the
 * browser never re-derives an accounting figure: a total the server computed is
 * the total, and a second opinion computed here would be one answer too many.
 * That rule is about AUTHORITY, not about addition — and two of the Stitch
 * references need a figure the API has no endpoint for:
 *
 *   Screen 07 pivots the ageing into a row per debtor, and draws a total column
 *   and a footing row. `/finance/receivables/ageing` returns one row per party
 *   PER BAND and no totals at all, so "what does this debtor owe in all" has to
 *   be the sum of the bands the server sent for them.
 *
 * **SO IT IS A SUBTOTAL OF ONE ANSWER, NEVER A SECOND ANSWER.** Every value
 * added here arrived in the same response, in the same currency, already through
 * the same record predicate; nothing is fetched, filtered or re-scoped to build
 * one. Where the server has the figure — an invoice's net, tax and gross, a
 * customer's outstanding and overdue — that figure is rendered and this file is
 * not used.
 *
 * **AND IT IS `BigInt`, NOT `Number`.** Minor units go past 2^53 long before
 * they go past a company's patience, and `Number('123456789012345678')` has
 * already lost the answer. Adding as `bigint` and formatting from the string is
 * exact for any amount a ledger can hold.
 */

/** The exact sum of minor-unit strings, as a minor-unit string. */
export function sumMinor(values: readonly string[]): string {
  return values.reduce((total, value) => total + BigInt(value), 0n).toString();
}

/**
 * A proportion in 0–1, for a bar's WIDTH — never for a figure on screen.
 *
 * Computed in `bigint` to a thousandth and only then divided, so the ratio is
 * not a float multiplication of two floats. A zero or absent whole gives 0
 * rather than a NaN width.
 */
export function fractionOf(part: string, whole: string): number {
  const divisor = BigInt(whole);
  if (divisor === 0n) return 0;
  const permille = (BigInt(part) * 1000n) / divisor;
  return Number(permille) / 1000;
}

/**
 * The reference draws initials in a 28px tile beside every organisation.
 *
 * The first letter of each of the first two words, which is a way of writing the
 * name the caller already has — not an identity, not an avatar and not a lookup.
 */
export function initialsOf(name: string): string {
  const words = name
    .split(/\s+/u)
    .map((word) => word.replace(/[^\p{L}\p{N}]/gu, ''))
    .filter((word) => word !== '');
  const first = words[0]?.[0] ?? '?';
  const second = words[1]?.[0] ?? '';
  return `${first}${second}`.toUpperCase();
}

/**
 * How many days there are between two `YYYY-MM-DD` days, whole and signed.
 *
 * Used only to say "14 days past due" beside a date the server sent, which is
 * the reference's own caption. It decides nothing: whether an invoice is overdue
 * is the server's `status`, and this never contradicts it.
 */
export function daysBetween(fromDay: string, toDay: string): number | null {
  const from = Date.parse(`${fromDay}T00:00:00Z`);
  const to = Date.parse(`${toDay}T00:00:00Z`);
  if (Number.isNaN(from) || Number.isNaN(to)) return null;
  return Math.round((to - from) / 86_400_000);
}

/** Today as `YYYY-MM-DD`, for prefilling a date field the operator may change. */
export function today(): string {
  return new Date().toISOString().slice(0, 10);
}

/** `342 kB`, as the reference captions an attachment. */
export function kilobytes(bytes: number): string {
  return `${String(Math.max(1, Math.round(bytes / 1024)))} kB`;
}
